import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaTrashAlt } from 'react-icons/fa';

interface IPProtection {
    id: string;
    server_name: string;
    ip_address: string;
}

const Protection: React.FC = () => {
    const { product_id } = useParams<{ product_id: string }>();
    const [ipProtections, setIpProtections] = useState<IPProtection[]>([]);
    const [serverName, setServerName] = useState('');
    const [ipAddress, setIpAddress] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/protection/${product_id}`, {
            credentials: 'include',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to fetch IP protections');
                }
                return response.json();
            })
            .then((data) => setIpProtections(data))
            .catch((error) => console.error('Error fetching IP protections:', error));
    }, [product_id]);

    if (!product_id) {
        return (
            <div className="text-center text-red-500">
                Product ID is missing. Please navigate to this page properly.
            </div>
        );
    }

    const addIpProtection = () => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/protection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                product_id,
                server_name: serverName,
                ip_address: ipAddress,
            }),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to add IP protection');
                }
                return response.json();
            })
            .then((newIp) => {
                setIpProtections([...ipProtections, newIp]);
                setServerName('');
                setIpAddress('');
                setIsModalOpen(false);
            })
            .catch((error) => console.error('Error adding IP protection:', error));
    };

    const deleteIpProtection = (id: string) => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/protection/${id}`, {
            method: 'DELETE',
            credentials: 'include',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to delete IP protection');
                }
                setIpProtections(ipProtections.filter((ip) => ip.id !== id));
            })
            .catch((error) => console.error('Error deleting IP protection:', error));
    };

    return (
        <div className="min-h-[calc(100vh-130px)] bg-gray-900 text-white flex justify-center items-baseline">
            <div className="w-3/5 bg-gradient-to-r from-slate-800 to-gray-800 rounded-lg shadow-lg p-6 mt-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-3xl font-bold" style={{ fontFamily: 'Tektur, serif' }}>
                        IP Protection
                    </h2>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="bg-green-700 text-white px-4 py-2 rounded font-bold shadow-lg hover:shadow-[0_0_10px_2px_rgba(21,128,61,0.5)] transition"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                        Add New IP
                    </button>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                    {ipProtections.length > 0 ? (
                        <ul className="space-y-2">
                            {ipProtections.map((ip, index) => (
                                <li key={ip.id} className="flex justify-between items-center bg-gray-800 p-2 rounded">
                                    <div className="flex flex-col">
                                        <span
                                            className="font-bold text-white text-lg"
                                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                                        >
                                            {ip.server_name}
                                        </span>
                                        <span
                                            className="text-gray-400 font-semibold"
                                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                                        >
                                            {ip.ip_address}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => deleteIpProtection(ip.id)}
                                        className="bg-red-800 px-4 py-2.5 rounded text-white shadow-lg hover:shadow-[0_0_10px_2px_rgba(203,27,27,0.5)] transition"
                                    >
                                        <FaTrashAlt size={22} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p
                            className="flex text-gray-400 font-semibold justify-center"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                            You have not yet added an IP Address
                        </p>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-10">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-1/3 text-white">
                        <h2
                            className="text-lg font-bold mb-4"
                            style={{ fontFamily: 'Montserrat, sans-serif', textAlign: 'center' }}
                        >
                            Add IP Address
                        </h2>
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Server Name"
                                value={serverName}
                                onChange={(e) => setServerName(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 font-semibold rounded px-4 py-2 text-white mb-2"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            />
                            <input
                                type="text"
                                placeholder="IP Address"
                                value={ipAddress}
                                onChange={(e) => setIpAddress(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 font-semibold rounded px-4 py-2 text-white"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            />
                        </div>
                        <div className="flex justify-end gap-4">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="bg-gray-600 px-4 py-2 rounded font-bold shadow-lg hover:shadow-[0_0_10px_2px_rgba(107,114,128,0.5)] transition"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                CANCEL
                            </button>
                            <button
                                onClick={addIpProtection}
                                className="bg-green-700 px-4 py-2 rounded text-white font-bold shadow-lg hover:shadow-[0_0_10px_2px_rgba(21,128,61,0.5)] transition"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                ADD
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Protection;